import { useState } from 'react'
import { Card, Divider, Tag } from 'antd'
import { DFM_SOURCE_TYPE_3D } from '@Admin/AdminQuotingPage/constants'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import ImDfmDragHoverIcon from '@Admin/AdminQuotingPage/ImDfmSlidesPanel/components/ImDfmDragHoverIcon'

import { getUnsavedImDfm3dSlides } from '~/redux-modules/unsaved-im-dfm-3d-slides/selector'
import Box from '~/core/Box'
import Tooltip from '~/core/Tooltip'
import Text from '~/core/Text'
import SlideActions from '../SlideActions'
import { TAG_STYLE } from '../ActiveSlide/ActiveSlideTitle/TagLabel'
import ThreeDTag from '../ActiveSlide/ActiveSlideTitle/ThreeDTag'
import { MessageOverview } from '../InactiveUnsaved3dSlide'
import { DFM_STATES, NO_ASSIGNED_TOPIC, ImDfmStateType } from '../constants'
import { Is2dDfmSlideReady_is2dDfmSlideReady as DfmFeedbackStatusType } from '../../__generated__/Is2dDfmSlideReady'
import { UnsavedSlideStateType } from '~/redux-modules/unsaved-im-dfm-3d-slides/reducer'

import InactiveSaved3dSlideTags from './InactiveSaved3dSlideTags'
import StatusOverviewIcon from './StatusOverviewIcon'
import styles from './styles.css'

type InactiveSlideTitlePropsType = Pick<
    InactiveSlidePropsType,
    | 'dfmFeedbackId'
    | 'idx'
    | 'is3dDfmSlide'
    | 'isPublished'
    | 'slideStatus'
    | 'status'
    | 'topic'
    | 'title'
    | 'isImQuoteType'
    | 'isNonImDfmEnabled'
    | 'isHighTouch'
>

const InactiveSlideTitle = (props: InactiveSlideTitlePropsType) => {
    const {
        dfmFeedbackId,
        idx,
        is3dDfmSlide,
        isPublished,
        slideStatus,
        status,
        topic,
        title,
        isImQuoteType,
        isNonImDfmEnabled,
        isHighTouch,
    } = props

    const is2dDfmGenerating = slideStatus?.loading
    const is2dDfmFailed = Boolean(slideStatus?.error)

    const isLabelNoAssignedTopic = topic?.label === NO_ASSIGNED_TOPIC
    let topicLabel = `Slide ${idx}`

    if (topic?.label && !isLabelNoAssignedTopic) {
        topicLabel = topic?.label
    }

    if (isNonImDfmEnabled && isHighTouch && !isImQuoteType) {
        topicLabel = title || `Slide ${idx}`
    }

    return (
        <Box justifyContent="between">
            <Box justifyContent="between" width="100%">
                <Text
                    data-test-target="search-name"
                    color="blackAlpha65"
                    fontSize="16px"
                    fontWeight={500}
                >
                    {!isImQuoteType && isHighTouch && isNonImDfmEnabled
                        ? title || 'No title specified'
                        : topicLabel}
                </Text>
                <div>
                    {is3dDfmSlide && <ThreeDTag />}
                    {!isPublished && (
                        <Tag
                            data-test-target="im-dfm-pending-release-tag"
                            style={TAG_STYLE}
                        >
                            Pending release
                        </Tag>
                    )}
                </div>
            </Box>
            {is3dDfmSlide && (
                <InactiveSaved3dSlideTags
                    hasGenerated2dDfmFailed={is2dDfmFailed}
                    is2dDfmGenerating={is2dDfmGenerating}
                />
            )}
            <SlideActions
                dfmFeedbackId={dfmFeedbackId}
                idx={idx}
                is3dDfmSlide={is3dDfmSlide}
                status={status}
            />
        </Box>
    )
}

type StatusOverviewPropsType = Pick<
    InactiveSlidePropsType,
    'status' | 'hovering'
>

export const StatusOverview = (props: StatusOverviewPropsType) => {
    const { hovering = false, status } = props

    if (!status) {
        return null
    }

    return (
        <ImDfmDragHoverIcon hovering={hovering}>
            <StatusOverviewIcon status={status} />
            <Text
                color={DFM_STATES[status] ? 'blackAlpha65' : 'blackAlpha45'}
                data-test-target="im-dfm-status"
                fontSize="14px"
                fontWeight={300}
            >
                {DFM_STATES[status]}
            </Text>
        </ImDfmDragHoverIcon>
    )
}

type InactiveSlidePropsType = {
    dfmFeedbackId: string
    hovering?: boolean
    idx: string
    is3dDfmSlide?: boolean
    isPublished: boolean
    message?: string
    onClick: () => void
    slideStatus?: DfmFeedbackStatusType
    sourceType?: string | null
    status: ImDfmStateType | null
    topic: { id: string; label: string } | null
    title: string | null
    isImQuoteType: boolean
    isNonImDfmEnabled: boolean
    isHighTouch: boolean
}

type ParamsType = {
    id: string
}

const InactiveSlide = (props: InactiveSlidePropsType) => {
    const {
        dfmFeedbackId,
        idx,
        isPublished,
        onClick,
        message,
        slideStatus,
        sourceType,
        status,
        topic,
        title,
        isImQuoteType,
        isNonImDfmEnabled,
        isHighTouch,
    } = props

    const { id: qmrId = '' } = useParams<ParamsType>()

    const [hovering, setHovering] = useState<boolean>(false)
    const unsavedImDfm3dSlides = useSelector((state: UnsavedSlideStateType) => {
        if (state.unsavedImDfm3dSlides) {
            return getUnsavedImDfm3dSlides(state, qmrId)
        }
    })

    const is3dDfmSlide = sourceType === DFM_SOURCE_TYPE_3D

    const haveUnsaved3dSlide = unsavedImDfm3dSlides?.length > 0
    const toolTipTitle = haveUnsaved3dSlide
        ? 'Other slides cannot be selected while you have an unsaved 3D slide.'
        : ''

    return (
        <Card
            className={styles.inactiveSlide}
            data-test-target={`im-dfm-slide-${idx}`}
            onClick={!haveUnsaved3dSlide ? onClick : undefined}
            size="small"
            style={{
                marginBottom: '8px',
                borderRadius: '4px',
                width: '100%',
                padding: '4px',
                opacity: haveUnsaved3dSlide ? 0.5 : 1,
            }}
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
        >
            <Tooltip
                title={toolTipTitle}
                overlayStyle={{
                    minWidth: '18%',
                }}
            >
                <InactiveSlideTitle
                    dfmFeedbackId={dfmFeedbackId}
                    idx={idx}
                    is3dDfmSlide={is3dDfmSlide}
                    isPublished={isPublished}
                    slideStatus={slideStatus}
                    status={status}
                    topic={topic}
                    title={title}
                    isImQuoteType={isImQuoteType}
                    isNonImDfmEnabled={isNonImDfmEnabled}
                    isHighTouch={isHighTouch}
                />
                <Divider style={{ margin: 4 }} />
                {is3dDfmSlide && <MessageOverview messageBody={message} />}
                <StatusOverview status={status} hovering={hovering} />
            </Tooltip>
        </Card>
    )
}

export default InactiveSlide
